import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { Label } from 'flowbite-react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useAuth } from '@restworld/feature-authentication';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrganizationsService } from '@restworld/data-services';
import { Organization } from '@restworld/utility-types';
import { RadioGroup, Select } from '@restworld/ui-ds-base';
import { TextArea } from '@restworld/ui-ds';

export default function SubscriptionCancellationFeedback() {
  const { t } = useTranslation('payment-and-invoices');
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { user: loggedInUser } = useAuth();
  const organizationsService = useOrganizationsService();
  const [organization, setOrganization] = useState<Organization | null>(null);

  const showOrganization = useCallback(() => {
    organizationsService
      .showOrganization(loggedInUser?.organization_id, 'employerApp:cancellationFeedback')
      .then((res) => {
        setOrganization(res.data);
      })
      .catch((error) => console.error({ error }));
  }, [organizationsService, loggedInUser]);

  useEffect(() => {
    showOrganization();
  }, [showOrganization]);

  const feedbackValues = useMemo(
    () => [
      {
        question: t('are_you_satisfied_with_the_service'),
        column: 'service_satisfaction',
        options: [
          // { key: 'service_satisfaction_0', label: '0' },
          { key: 'service_satisfaction_1', label: '1' },
          { key: 'service_satisfaction_2', label: '2' },
          { key: 'service_satisfaction_3', label: '3' },
          { key: 'service_satisfaction_4', label: '4' },
          { key: 'service_satisfaction_5', label: '5' },
          { key: 'service_satisfaction_6', label: '6' },
          { key: 'service_satisfaction_7', label: '7' },
          { key: 'service_satisfaction_8', label: '8' },
          { key: 'service_satisfaction_9', label: '9' },
          { key: 'service_satisfaction_10', label: '10' },
        ],
        input: InputType.COLOURED_RADIO,
      },
      {
        question: t('why_are_you_canceling_the_search'),
        column: 'motive_for_cancelling_subscription',
        options: [
          {
            key: 'motive_for_cancelling_subscription_hired_through_restworld',
            label: t('slot_cancellation_reason.i_hired_through_restworld'),
          },
          {
            key: 'motive_for_cancelling_subscription_hired_through_other_channel',
            label: t('slot_cancellation_reason.i_hired_through_other_channels'),
          },
          {
            key: 'motive_for_cancelling_subscription_not_searching_again',
            label: t('slot_cancellation_reason.i_am_no_longer_looking_for_the_resources'),
          },
          {
            key: 'motive_for_cancelling_subscription_expensive_service',
            label: t('slot_cancellation_reason.service_is_too_expensive'),
          },
          {
            key: 'motive_for_cancelling_subscription_try_alternative_service',
            label: t('slot_cancellation_reason.i_want_to_try_an_alternative_to_your_service'),
          },
          {
            key: 'motive_for_cancelling_subscription_not_enough_candidates',
            label: t('slot_cancellation_reason.i_did_not_receive_enough_candidates'),
          },
          {
            key: 'motive_for_cancelling_subscription_unsatisfactory_candidates',
            label: t('slot_cancellation_reason.the_candidates_you_sent_me_did_not_meet_my_expectations'),
          },
          {
            key: 'motive_for_cancelling_subscription_unsatisfied_assistance_from_consultant',
            label: t('slot_cancellation_reason.i_am_not_satisfied_with_the_support_from_my_manager'),
          },
        ],
        input: InputType.SELECT,
      },
      {
        question: t('are_you_satisfied_with_the_assistance_received_from_your_restworld_manager'),
        column: 'assistance_satisfaction',
        options: [
          // { key: 'assistance_satisfaction_0', label: '0' },
          { key: 'assistance_satisfaction_1', label: '1' },
          { key: 'assistance_satisfaction_2', label: '2' },
          { key: 'assistance_satisfaction_3', label: '3' },
          { key: 'assistance_satisfaction_4', label: '4' },
          { key: 'assistance_satisfaction_5', label: '5' },
          { key: 'assistance_satisfaction_6', label: '6' },
          { key: 'assistance_satisfaction_7', label: '7' },
          { key: 'assistance_satisfaction_8', label: '8' },
          { key: 'assistance_satisfaction_9', label: '9' },
          { key: 'assistance_satisfaction_10', label: '10' },
        ],
        input: InputType.COLOURED_RADIO,
      },
      {
        question: t('would_you_reactivate_the_service_in_the_future'),
        column: 'reactivate_service',
        options: [
          { key: 'reactivate_service_yes', label: t('yes') },
          { key: 'reactivate_service_no', label: t('no') },
        ],
        input: InputType.RADIO,
      },
      {
        question: t('would_you_recommend_restworld_to_a_friend'),
        column: 'service_referral_rating',
        options: [
          // { key: 'service_referral_rating_0', label: '0' },
          { key: 'service_referral_rating_1', label: '1' },
          { key: 'service_referral_rating_2', label: '2' },
          { key: 'service_referral_rating_3', label: '3' },
          { key: 'service_referral_rating_4', label: '4' },
          { key: 'service_referral_rating_5', label: '5' },
          { key: 'service_referral_rating_6', label: '6' },
          { key: 'service_referral_rating_7', label: '7' },
          { key: 'service_referral_rating_8', label: '8' },
          { key: 'service_referral_rating_9', label: '9' },
          { key: 'service_referral_rating_10', label: '10' },
        ],
        input: InputType.COLOURED_RADIO,
      },
    ],
    [t]
  );

  return (
    <div tw="flex flex-col gap-3">
      <h5 tw="text-base text-neutral-700 lg:(text-center)">{t('before_canceling_tell_us_how_it_went')}</h5>
      {feedbackValues?.map((val, index) => {
        return (
          <div key={index}>
            <div tw="flex flex-col">
              <Label tw="text-base font-medium mb-2 mt-4">{val.question}</Label>
            </div>
            <Controller
              name={val.column}
              control={control}
              render={({ field: { value, onChange } }) => {
                if (val.input === InputType.COLOURED_RADIO || val.input === InputType.RADIO) {
                  return (
                    <div tw="flex flex-col items-start">
                      <RadioGroup
                        options={val.options.map((option) => ({
                          ...option,
                          id: option.key,
                          colorStyles:
                            Number(option.label) >= 9
                              ? tw`bg-green-50 text-green-700 border-green-700 hover:(bg-green-100)`
                              : Number(option.label) >= 7
                              ? tw`bg-orange-50 text-orange-700 border-orange-700 hover:(bg-orange-100)`
                              : tw`bg-red-50 text-red-700 border-red-700 hover:(bg-red-100)`,
                        }))}
                        id={val.column}
                        orientation="horizontal"
                        hideIndicator={val.input === InputType.RADIO ? false : true}
                        borderless={val.input === InputType.RADIO}
                        value={value}
                        onChange={(newValue) => {
                          onChange(newValue);
                        }}
                        isFullWidth={val.input === InputType.COLOURED_RADIO}
                      />
                      {val.input === InputType.COLOURED_RADIO && (
                        <div tw="flex items-center justify-between w-full mt-2">
                          <HelperText>{t('not_at_all')}</HelperText>{' '}
                          <HelperText>
                            {val.column === 'service_referral_rating' ? t('certainly') : t('very_much')}
                          </HelperText>
                        </div>
                      )}
                    </div>
                  );
                } else if (val.input === InputType.SELECT) {
                  return (
                    <Select
                      fullwidth
                      options={val.options.map((option) => ({ ...option, value: option.key }))}
                      placeholder={t('select_reason')}
                      value={value}
                      onChange={(newValue) => {
                        onChange(newValue);
                      }}
                    />
                  );
                }
              }}
            />
            {!!errors?.[val.column] && (
              <div tw="flex flex-row gap-1 mt-1">
                <IoInformationCircleOutline tw="text-red-500" />
                <Label tw="text-xs text-red-500">{(errors?.[val.column] as FieldError)?.message}</Label>
              </div>
            )}
          </div>
        );
      })}
      <Label tw="text-lg mb-2 mt-4">{t('would_you_like_to_leave_an_additional_comment')}</Label>
      <Controller
        name="additional_feedback"
        control={control}
        render={({ field: { value, onChange } }) => (
          <TextArea
            id="additional_feedback"
            value={value}
            onChange={(v) => {
              onChange(v);
            }}
            name="additional_feedback"
            placeholder={t('your_feedback')}
            rows={6}
            twStyle={tw`border-neutral-90 focus:(!border-neutral-100 !ring-neutral-100) bg-neutral-10`}
          />
        )}
      />
    </div>
  );
}

const InputType = {
  RADIO: 'radio',
  COLOURED_RADIO: 'coloured-radio',
  SELECT: 'select',
};

const HelperText = styled.span`
  ${tw`text-sm text-neutral-100 block`}
`;
const RatingRadioContainer = styled.div<{ rating?: string }>(({ rating }) => {
  const basicStyling = tw`flex flex-row items-center gap-0.5 p-1.5 rounded`;

  const ratingNumber = parseInt(rating, 10);
  if (isNaN(ratingNumber)) return basicStyling;

  return [
    basicStyling,
    ratingNumber >= 1 && ratingNumber < 7 && tw`bg-red-100`,
    ratingNumber >= 7 && ratingNumber < 9 && tw`bg-yellow-100`,
    ratingNumber >= 9 && tw`bg-green-100`,
  ];
});
